import { useCallback, useContext, useEffect, useState } from "preact/hooks";
import { delay } from "../utils/async";
import { Disposable } from "../utils/disposable";
import { Dispatcher } from "../utils/events/dispatcher";
import { DataProvider, DataProviderQueryResult, NON_EXISTING } from "./data_provider";
import { DataContext } from "./react_context";

class StateCache {
  private dispatcher = new Dispatcher();
  private ids_: string[];
  private state_!: DataResult<unknown>;
  private currentAttachment: Disposable | null = null;

  onUpdate = this.dispatcher.signal();

  constructor(ids: string[], private provider: DataProvider) {
    this.ids_ = ids;
    this.fetch();
  }

  private fetch() {
    const result = this.provider.get(this.ids_);

    this.state_ = makeDataResultObject(result);
    this.dispatcher.emit(this.onUpdate);

    if (this.currentAttachment) {
      this.currentAttachment.dispose();
    }

    const disposable = new Disposable();
    this.currentAttachment = disposable;
    result.onUpdate.attach(() => {
      this.dispatcher.emit(this.onUpdate);
    }, disposable);
  }

  updateIds(ids: string[]) {
    this.ids_ = ids;
    this.fetch();
  }

  get ids() {
    return this.ids_;
  }

  get state() {
    return this.state_;
  }
}

const stateCache = new WeakMap<object, StateCache>();

export function useData<T>(ids: string[]): DataResult<T> {
  const dataContext = useContext(DataContext);
  if (!dataContext) throw new Error("Data hook not used inside of a DataContext.");

  const [key] = useState<object>({});
  let cached = stateCache.get(key);
  if (!cached) {
    const result = dataContext.provider.get(ids);
    const state = makeDataResultObject(result);
    cached = new StateCache(ids, dataContext.provider);
    stateCache.set(ids, cached);
  }

  if (!areIdsEqual(ids, cached.ids)) {
    // update cache since IDs changed
    cached.updateIds(ids);
  }

  const [, setDataState] = useState<unknown>({});

  useCallback(() => {
    const disposable = new Disposable();
    cached!.onUpdate.attach(() => {
      setDataState({});
    }, disposable);

    () => {
      // clean up
      disposable.dispose();
    };
  }, [cached]);

  if (cached.state.isLoading) {
    throw cached.onUpdate.next();
  }

  return cached.state as DataResult<T>;
}

function areIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

function makeDataResultObject<T>(queryResult: DataProviderQueryResult): DataResult<T> {
  const value = queryResult.onUpdate.lastValue as T | typeof NON_EXISTING | undefined;
  const isLoading = !queryResult.onUpdate.hasValue;
  const exists = !isLoading && value !== NON_EXISTING;
  return {
    value: exists ? value : undefined,
    isLoading,
    exists,
  };
}

interface DataResult<TValue> {
  value: TValue | undefined;
  isLoading: boolean;
  exists: boolean;
}
